/**
 * QuizList (Sprint 6 B4) — list quiz của 1 course.
 *
 * Route: /courses/:slug/quizzes
 * UX:
 *   - Breadcrumb: Home → Môn học → {course} → Bài kiểm tra
 *   - Card grid với title + duration + threshold + max_attempts + webcam tag
 *   - Click "Bắt đầu thi" → navigate /quizzes/:id/consent (B6 — Sprint 6 placeholder)
 *   - Chưa enroll → empty state
 */

import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  Result,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  ClockCircleOutlined,
  EyeOutlined,
  HomeOutlined,
  RightOutlined,
  TrophyOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCourseDetail } from "@/api/catalog";
import { useEnrollmentFor } from "@/api/enrollment";
import { useQuizzes } from "@/api/assessment";
import { useAuthStore } from "@/stores/auth";

const { Title, Text, Paragraph } = Typography;

export function QuizList() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: course, isLoading: courseLoading } = useCourseDetail(slug);
  const { enrollment } = useEnrollmentFor(slug);
  const { data: quizzes, isLoading, error } = useQuizzes(slug);
  // Giảng viên/admin xem trước quiz không cần enroll (review nội dung). Họ KHÔNG
  // "Bắt đầu thi" (tránh tạo Attempt rác — BE cũng chặn) mà mở trình soạn admin.
  const role = useAuthStore((s) => s.user?.role);
  const isStaff =
    role === "instructor" || role === "tenant_admin" || role === "superadmin";

  if (courseLoading || isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error || !course) {
    return (
      <Result
        status="404"
        title="Không tìm thấy môn học"
        extra={<Link to="/courses">← Về danh sách môn học</Link>}
      />
    );
  }

  const isEnrolled = enrollment?.status === "enrolled" || enrollment?.status === "completed";
  const canView = isEnrolled || isStaff;

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/"><HomeOutlined /></Link> },
          { title: <Link to="/courses">Môn học</Link> },
          { title: <Link to={`/courses/${slug}`}>{course.name}</Link> },
          { title: "Bài kiểm tra" },
        ]}
      />

      <Title level={2}>
        <TrophyOutlined /> Bài kiểm tra — {course.name}
      </Title>

      {!isEnrolled && !isStaff && (
        <Alert
          type="warning"
          showIcon
          message="Bạn cần đăng ký môn học trước khi xem bài kiểm tra."
          action={
            <Link to={`/courses/${slug}`}>
              <Button size="small" type="primary">
                Đăng ký
              </Button>
            </Link>
          }
          style={{ marginBottom: 24 }}
        />
      )}

      {isStaff && !isEnrolled && (
        <Alert
          type="info"
          showIcon
          icon={<EyeOutlined />}
          message="Chế độ xem trước (giảng viên)"
          description="Bạn đang xem danh sách bài kiểm tra để duyệt nội dung. Mở 'Xem câu hỏi' để soạn/kiểm tra chi tiết. Không tạo lượt làm bài."
          style={{ marginBottom: 24 }}
        />
      )}

      {canView && (!quizzes || quizzes.length === 0) && (
        <Empty
          description="Môn học này chưa có bài kiểm tra nào."
          style={{ padding: 48 }}
        />
      )}

      {canView && quizzes && quizzes.length > 0 && (
        <Row gutter={[16, 16]} data-testid="quiz-grid">
          {quizzes.map((quiz) => (
            <Col xs={24} sm={12} lg={8} key={quiz.id}>
              <Card
                hoverable
                title={quiz.title}
                actions={[
                  isStaff ? (
                    <Link key="preview" to={`/admin/courses/${slug}/quizzes/${quiz.id}`}>
                      <Button type="default" icon={<EyeOutlined />}>
                        Xem câu hỏi
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      key="start"
                      type="primary"
                      icon={<RightOutlined />}
                      onClick={() => navigate(`/quizzes/${quiz.id}/consent`)}
                    >
                      Bắt đầu thi
                    </Button>
                  ),
                ]}
              >
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  {quiz.description && (
                    <Paragraph type="secondary" ellipsis={{ rows: 2 }}>
                      {quiz.description}
                    </Paragraph>
                  )}
                  <Space wrap>
                    <Tag color="blue" icon={<ClockCircleOutlined />}>
                      {quiz.duration_display}
                    </Tag>
                    <Tag color="green">
                      Pass {quiz.pass_threshold_pct}%
                    </Tag>
                    {quiz.max_attempts > 0 && (
                      <Tag>
                        Tối đa {quiz.max_attempts} lần
                      </Tag>
                    )}
                    {quiz.webcam_required && (
                      <Tag color="orange" icon={<VideoCameraOutlined />}>
                        Cần webcam
                      </Tag>
                    )}
                  </Space>
                  <Text type="secondary">
                    Tổng điểm: <strong>{quiz.total_points}</strong>
                  </Text>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

export default QuizList;
